import { useState } from "react"
import type { Dayjs } from "dayjs"
import {
  AuditOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons"
import {
  Button,
  DatePicker,
  Flex,
  Form,
  Input,
  Select,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd"
import type { TableProps } from "antd"
import { listStaffOrderCleanupOpLogs } from "~/api/staff"
import { CleanupLogDetailModal } from "./DetailModal"
import {
  cleanupOperationColor,
  cleanupOperationLabel,
  cleanupStatusColor,
  cleanupStatusLabel,
} from "./presentation"
import { formatAmount, formatDateTime, optionalValue } from "~/common/utils"
import { MOBILE_PHONE_PATTERN } from "~/common/constants"
import { usePagedQuery } from "~/pages/Staff/usePagedQuery"
import styles from "./index.css"

const { Text, Title } = Typography
const { RangePicker } = DatePicker

type CleanupLogFilterForm = {
  createdRange?: [Dayjs, Dayjs]
  operation?: API.Staff.StaffOrderCleanupOperation
  orderNo?: string
  staffMobile?: string
  userId?: string
}

const operationOptions = Object.entries(cleanupOperationLabel).map(
  ([value, label]) => ({ value, label }),
)

export const StaffOrderCleanupHistory = () => {
  const [form] = Form.useForm<CleanupLogFilterForm>()
  const { total, loading, search, refresh, tableProps } = usePagedQuery<
    API.Staff.StaffOrderCleanupOpLogListParams,
    API.Staff.StaffOrderCleanupOpLogSummary
  >({
    fetchPage: listStaffOrderCleanupOpLogs,
    initialFilters: {},
  })
  const [selectedOpLogId, setSelectedOpLogId] = useState<string | null>(null)

  const applyFilters = async () => {
    const values = await form.validateFields()
    const createdRange = values.createdRange

    search({
      order_no: optionalValue(values.orderNo),
      staff_mobile: optionalValue(values.staffMobile),
      user_id: optionalValue(values.userId),
      operation: values.operation,
      created_from: createdRange?.[0].toISOString(),
      created_to: createdRange?.[1].toISOString(),
    })
  }

  const resetFilters = () => {
    form.resetFields()
    search({})
  }

  const columns: TableProps<API.Staff.StaffOrderCleanupOpLogSummary>["columns"] =
    [
      {
        title: "操作时间",
        dataIndex: "created_t",
        width: 180,
        render: formatDateTime,
      },
      {
        title: "操作类型",
        dataIndex: "operation",
        width: 190,
        render: (value: API.Staff.StaffOrderCleanupOperation) => (
          <Tag color={cleanupOperationColor[value]}>
            {cleanupOperationLabel[value]}
          </Tag>
        ),
      },
      {
        title: "状态",
        dataIndex: "status",
        width: 150,
        render: (value: API.Staff.StaffOrderCleanupStatus) => (
          <Tag color={cleanupStatusColor[value]}>
            {cleanupStatusLabel[value]}
          </Tag>
        ),
      },
      {
        title: "订单号",
        dataIndex: "order_no",
        width: 210,
        render: (value: string) => <Text copyable>{value}</Text>,
      },
      {
        title: "订单金额",
        dataIndex: "amount",
        width: 110,
        render: formatAmount,
      },
      {
        title: "操作Staff",
        width: 190,
        render: (_, record) => (
          <Flex
            gap={2}
            vertical
          >
            <Text>{record.staff_mobile}</Text>
            <Text
              ellipsis
              type="secondary"
            >
              {record.staff_id}
            </Text>
          </Flex>
        ),
      },
      {
        title: "订单用户ID",
        dataIndex: "user_id",
        ellipsis: true,
        width: 210,
        render: (value?: string | null) => value || "-",
      },
      {
        title: "退款信息",
        width: 230,
        render: (_, record) =>
          record.out_refund_no || record.wechat_refund_status ? (
            <Flex
              gap={3}
              vertical
            >
              <Text>{record.out_refund_no || "-"}</Text>
              <Text type="secondary">
                微信状态：{record.wechat_refund_status || "-"}
              </Text>
            </Flex>
          ) : (
            "-"
          ),
      },
      {
        title: "确认到账",
        width: 180,
        render: (_, record) =>
          record.confirmed_at ? (
            <Flex
              gap={3}
              vertical
            >
              <Text>{formatDateTime(record.confirmed_at)}</Text>
              <Text type="secondary">
                {record.confirmed_by_staff_mobile || "-"}
              </Text>
            </Flex>
          ) : (
            "-"
          ),
      },
      {
        title: "失败原因",
        dataIndex: "error_message",
        ellipsis: true,
        width: 220,
        render: (value?: string | null) =>
          value ? (
            <Tooltip title={value}>
              <Text type="danger">{value}</Text>
            </Tooltip>
          ) : (
            "-"
          ),
      },
      {
        title: "操作",
        fixed: "right",
        width: 80,
        render: (_, record) => (
          <Button
            type="link"
            onClick={() => setSelectedOpLogId(record.id)}
          >
            详情
          </Button>
        ),
      },
    ]

  return (
    <Flex
      className={styles.page}
      gap={18}
      vertical
    >
      <Flex
        gap={6}
        vertical
      >
        <Title level={2}>危险操作记录</Title>
        <Text type="secondary">
          查看测试订单退款、人工确认及删除操作的完整审计记录。
        </Text>
      </Flex>

      <section className={styles.filterPanel}>
        <Form<CleanupLogFilterForm>
          form={form}
          layout="vertical"
          onFinish={() => void applyFilters()}
        >
          <div className={styles.filterGrid}>
            <Form.Item
              label="订单号"
              name="orderNo"
              rules={[{ max: 32, message: "订单号不能超过32个字符" }]}
            >
              <Input
                allowClear
                placeholder="请输入订单号"
              />
            </Form.Item>
            <Form.Item
              label="操作类型"
              name="operation"
            >
              <Select
                allowClear
                options={operationOptions}
                placeholder="全部操作类型"
              />
            </Form.Item>
            <Form.Item
              label="Staff手机号"
              name="staffMobile"
              rules={[
                {
                  pattern: MOBILE_PHONE_PATTERN,
                  message: "请输入正确的Staff手机号",
                },
              ]}
            >
              <Input
                allowClear
                maxLength={11}
                placeholder="请输入手机号"
              />
            </Form.Item>
            <Form.Item
              label="订单用户ID"
              name="userId"
            >
              <Input
                allowClear
                placeholder="请输入订单用户ID"
              />
            </Form.Item>
            <Form.Item
              className={styles.dateRangeField}
              label="操作时间"
              name="createdRange"
            >
              <RangePicker
                allowClear
                format="YYYY-MM-DD HH:mm"
                placeholder={["开始时间", "结束时间"]}
                showTime={{ format: "HH:mm" }}
              />
            </Form.Item>
          </div>
          <Flex
            className={styles.filterActions}
            gap={10}
            justify="flex-end"
          >
            <Button onClick={resetFilters}>重置</Button>
            <Button
              htmlType="submit"
              icon={<SearchOutlined />}
              loading={loading}
              type="primary"
            >
              查询
            </Button>
          </Flex>
        </Form>
      </section>

      <section className={styles.listPanel}>
        <Flex
          className={styles.listHeader}
          align="center"
          justify="space-between"
        >
          <Flex
            align="center"
            gap={8}
          >
            <AuditOutlined className={styles.sectionIcon} />
            <Title level={4}>操作记录</Title>
            <Text type="secondary">共 {total} 条</Text>
          </Flex>
          <Button
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={() => refresh()}
          >
            刷新
          </Button>
        </Flex>
        <Table
          columns={columns}
          {...tableProps}
          rowKey="id"
          scroll={{ x: 1_950 }}
        />
      </section>

      <CleanupLogDetailModal
        opLogId={selectedOpLogId}
        onClose={() => setSelectedOpLogId(null)}
      />
    </Flex>
  )
}
